import React, { useState, useEffect, useRef } from 'react';
import { Bell, Calendar, BookOpen, Trophy, CreditCard, Newspaper, Shield, AlertTriangle, UserPlus, Mail } from 'lucide-react';
import { notifInbox } from '../services/api';
import usePushNotifications from '../hooks/usePushNotifications';
import PushActivationBanner from './PushActivationBanner';

const NOTIF_ICONS = {
  weather: AlertTriangle, new_booking: Calendar, cancel_booking: Calendar,
  new_booking_individuel: Calendar, new_booking_collectif: Calendar,
  booking_confirmation: Calendar, booking_confirmed: Calendar, booking_cancelled_by_admin: Calendar,
  new_competition_registration: Trophy, cancel_competition_registration: Trophy,
  new_competition_available: Trophy, competition_reminder: Trophy,
  new_stage_registration: BookOpen, stage_reminder: BookOpen,
  new_payment: CreditCard, new_pack_purchase: CreditCard, cotisation_expiry: CreditCard,
  new_client: UserPlus, new_news: Newspaper, new_devis: Mail, family_request: UserPlus,
};

const NOTIF_COLORS = {
  weather: ['text-orange-500', 'bg-orange-50'], new_booking: ['text-blue-500', 'bg-blue-50'],
  new_booking_individuel: ['text-blue-500', 'bg-blue-50'], new_booking_collectif: ['text-blue-500', 'bg-blue-50'],
  cancel_booking: ['text-red-500', 'bg-red-50'], booking_confirmation: ['text-green-500', 'bg-green-50'],
  booking_confirmed: ['text-green-500', 'bg-green-50'],
  booking_cancelled_by_admin: ['text-red-500', 'bg-red-50'],
  new_competition_registration: ['text-purple-500', 'bg-purple-50'],
  new_competition_available: ['text-purple-500', 'bg-purple-50'],
  competition_reminder: ['text-amber-500', 'bg-amber-50'],
  new_stage_registration: ['text-teal-500', 'bg-teal-50'], stage_reminder: ['text-amber-500', 'bg-amber-50'],
  new_payment: ['text-green-500', 'bg-green-50'], new_pack_purchase: ['text-green-500', 'bg-green-50'],
  cotisation_expiry: ['text-orange-500', 'bg-orange-50'], new_client: ['text-blue-500', 'bg-blue-50'],
  new_news: ['text-indigo-500', 'bg-indigo-50'], new_devis: ['text-cyan-500', 'bg-cyan-50'],
  family_request: ['text-pink-500', 'bg-pink-50'],
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return "A l'instant";
  if (diff < 60) return `Il y a ${diff}min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  if (diff < 10080) return `Il y a ${Math.floor(diff / 1440)}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const NotificationInbox = ({ userId, target, bellSize = 20, className = '', userType = 'admin' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const { isSubscribed: pushSubscribed, subscribe: pushSubscribe, loading: pushLoading } = usePushNotifications(userId, userType);

  // Fetch count + notifications on mount and poll
  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      try {
        const countRes = await notifInbox.getUnreadCount(userId, target);
        setUnreadCount(countRes.data?.count || 0);
      } catch (e) { /* ignore */ }
      try {
        const listRes = await notifInbox.getNotifications(userId, target);
        setNotifications(listRes.data?.notifications || []);
      } catch (e) { /* ignore */ }
    };
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId, target]);

  // Mark all read + refresh when opening
  useEffect(() => {
    if (!isOpen || !userId) return;
    const doFetch = async () => {
      try {
        const listRes = await notifInbox.getNotifications(userId, target);
        setNotifications(listRes.data?.notifications || []);
      } catch (e) { /* ignore */ }
      if (unreadCount > 0) {
        setUnreadCount(0);
        try { await notifInbox.markAllRead(userId, target); } catch (e) { /* ignore */ }
      }
    };
    doFetch();
  }, [isOpen, userId, target]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={panelRef} data-testid="notification-inbox">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors"
        data-testid="notification-inbox-bell"
      >
        <Bell size={bellSize} className={unreadCount > 0 ? 'text-primary' : 'text-neutral-500'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse" data-testid="notification-inbox-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {!pushSubscribed && !pushLoading && unreadCount === 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-neutral-200 z-50 overflow-hidden" data-testid="notification-inbox-panel">
          {!pushSubscribed && <PushActivationBanner pushSubscribe={pushSubscribe} />}
          <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-primary" />
              <span className="font-semibold text-sm text-neutral-900">Notifications</span>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-neutral-400">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium">Aucune notification</p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => {
                  const IconComp = NOTIF_ICONS[notif.type] || Bell;
                  const colors = NOTIF_COLORS[notif.type] || ['text-neutral-500', 'bg-neutral-50'];
                  return (
                    <a
                      key={notif.id}
                      href={notif.url || '#'}
                      className="block px-4 py-3 border-b border-neutral-50 hover:bg-neutral-50 transition-colors"
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[1]}`}>
                          <IconComp size={16} className={colors[0]} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 line-clamp-1">{notif.title}</p>
                          <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{notif.body}</p>
                          <p className="text-[10px] text-neutral-400 mt-1">{formatTime(notif.created_at)}</p>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationInbox;
